// src/pages/dashboard/branch/settings/componenets/ServicesSettings.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../../i18n/config';
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiToggleLeft,
    FiToggleRight,
    FiSettings,
    FiStar,
    FiEye,
    FiShoppingCart,
    FiMessageSquare,
    FiBarChart,
    FiUsers,
    FiCreditCard,
    FiGlobe,
    FiSmartphone,
    FiWifi,
    FiSearch,
    FiDownload,
    FiTruck,
    FiMapPin,
    FiClock,
    FiFilter,
    FiAlertTriangle,
    FiDollarSign,
    FiTrendingUp,
    FiPercent,
    FiGift,
    FiUserPlus,
    FiLock,
    FiBell
} from 'react-icons/fi';
import Card from '../../../../../components/ui/Card';
import Button from '../../../../../components/ui/Button';
import InputField from '../../../../../components/ui/InputField';
import Badge from '../../../../../components/ui/Badge';
import Toggle from '../../../../../components/ui/Toggle';
import ConfirmDeleteModal from '../../../../../components/ui/ConfirmDeleteModal';
import { Branch } from '../../../../../utils/supabaseClient';
import {
    Feature,
    FeatureChild,
    getFeatureArName,
    getFeatureArDescription,
    getFeatureChildren,
    featureChildrenToJson,
    getFeatureChildArName,
    getFeatureChildArDescription,
    updateFeatureChildAr
} from '../../../../../types/custom';
import { featureService } from '../../../../../services/featureService';
import { uploadToSupabaseStorage } from '../../../../../utils/supabaseStorage';

type ServicesSettingsProps = {
    restaurant: Branch | null;
    updateRestaurant: (updates: Partial<Branch>) => void;
    branchId: string | undefined;
}

// Default feature templates that can be added to any branch
const FEATURE_TEMPLATES: Omit<Feature, 'id' | 'created_at' | 'updated_at' | 'branch_id'>[] = [
    {
        name: 'Online Ordering',
        description: 'Allow customers to place orders online through your website or app',
        icon: 'FiShoppingCart',
        children: [
            { id: 'delivery', name: 'Delivery', description: 'Enable delivery orders', enabled: false, icon: 'FiTruck' },
            { id: 'pickup', name: 'Pickup', description: 'Enable pickup orders', enabled: false, icon: 'FiMapPin' },
            { id: 'scheduled', name: 'Scheduled Orders', description: 'Allow customers to schedule orders', enabled: false, icon: 'FiClock' },
            { id: 'payment', name: 'Online Payment', description: 'Accept payments online', enabled: false, icon: 'FiCreditCard' }
        ]
    },
    {
        name: 'Digital Menu',
        description: 'QR code accessible digital menu for dine-in customers',
        icon: 'FiEye',
        children: [
            { id: 'qr_code', name: 'QR Code Menu', description: 'Generate QR codes for tables', enabled: false, icon: 'FiSmartphone' },
            { id: 'multilingual', name: 'Multi-language', description: 'Support multiple languages', enabled: false, icon: 'FiGlobe' },
            { id: 'dietary_filters', name: 'Dietary Filters', description: 'Filter by dietary preferences', enabled: false, icon: 'FiFilter' },
            { id: 'allergen_info', name: 'Allergen Information', description: 'Display allergen warnings', enabled: false, icon: 'FiAlertTriangle' }
        ]
    },
    {
        name: 'Customer Reviews',
        description: 'Collect and manage customer feedback and reviews',
        icon: 'FiMessageSquare',
        children: [
            { id: 'rating_system', name: 'Rating System', description: 'Star rating for items', enabled: false, icon: 'FiStar' },
            { id: 'written_reviews', name: 'Written Reviews', description: 'Text reviews from customers', enabled: false, icon: 'FiEdit2' },
            { id: 'review_moderation', name: 'Review Moderation', description: 'Approve reviews before publishing', enabled: false, icon: 'FiEye' },
            { id: 'review_responses', name: 'Review Responses', description: 'Respond to customer reviews', enabled: false, icon: 'FiMessageSquare' }
        ]
    },
    {
        name: 'Analytics & Reports',
        description: 'Track performance metrics and generate reports',
        icon: 'FiBarChart',
        children: [
            { id: 'sales_analytics', name: 'Sales Analytics', description: 'Track sales performance', enabled: false, icon: 'FiDollarSign' },
            { id: 'item_performance', name: 'Item Performance', description: 'Most popular items', enabled: false, icon: 'FiTrendingUp' },
            { id: 'customer_insights', name: 'Customer Insights', description: 'Customer behavior analysis', enabled: false, icon: 'FiUsers' },
            { id: 'export_reports', name: 'Export Reports', description: 'Download reports as PDF/Excel', enabled: false, icon: 'FiDownload' }
        ]
    },
    {
        name: 'Loyalty Program',
        description: 'Reward repeat customers with points and discounts',
        icon: 'FiStar',
        children: [
            { id: 'points_system', name: 'Points System', description: 'Earn points on purchases', enabled: false, icon: 'FiStar' },
            { id: 'member_discounts', name: 'Member Discounts', description: 'Special prices for members', enabled: false, icon: 'FiPercent' },
            { id: 'birthday_rewards', name: 'Birthday Rewards', description: 'Special birthday offers', enabled: false, icon: 'FiGift' },
            { id: 'referral_program', name: 'Referral Program', description: 'Rewards for referrals', enabled: false, icon: 'FiUserPlus' }
        ]
    },
    {
        name: 'Staff Management',
        description: 'Manage staff accounts and permissions',
        icon: 'FiUsers',
        children: [
            { id: 'role_permissions', name: 'Role & Permissions', description: 'Control access levels', enabled: false, icon: 'FiLock' },
            { id: 'shift_management', name: 'Shift Management', description: 'Schedule staff shifts', enabled: false, icon: 'FiClock' },
            { id: 'performance_tracking', name: 'Performance Tracking', description: 'Track staff performance', enabled: false, icon: 'FiBarChart' },
            { id: 'staff_notifications', name: 'Staff Notifications', description: 'Internal messaging system', enabled: false, icon: 'FiBell' }
        ]
    },
    {
        name: 'Payment',
        description: 'Make your payment using:',
        icon: 'FiCreditCard',
        children: [
            { id: 'omt', name: 'OMT', description: 'OMT payment integration', enabled: false, icon: 'FiCreditCard' }
        ]
    }
];

// Icon mapping for display
const ICON_MAP: { [key: string]: React.ComponentType } = {
    FiShoppingCart,
    FiEye,
    FiMessageSquare,
    FiBarChart,
    FiStar,
    FiUsers,
    FiSettings,
    FiCreditCard,
    FiGlobe,
    FiSmartphone,
    FiWifi,
    FiSearch,
    FiDownload,
    FiTruck,
    FiMapPin,
    FiClock,
    FiFilter,
    FiAlertTriangle,
    FiDollarSign,
    FiTrendingUp,
    FiPercent,
    FiGift,
    FiUserPlus,
    FiLock,
    FiBell
};

type IconInputMode = 'text' | 'image';

const isImageIconValue = (icon?: unknown): boolean => {
    if (typeof icon !== 'string') return false;
    const normalized = icon.trim();
    return normalized.startsWith('http') || normalized.startsWith('/') || normalized.startsWith('data:');
};

const determineIconMode = (icon?: unknown): IconInputMode => {
    return isImageIconValue(icon) ? 'image' : 'text';
};

const sanitizeIconValue = (icon?: unknown): string | undefined => {
    if (typeof icon !== 'string') return undefined;
    const trimmed = icon.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const prepareChildrenForSave = (children: FeatureChild[]): FeatureChild[] => {
    return children.map(child => ({
        ...child,
        icon: sanitizeIconValue(child.icon)
    }));
};

interface CustomFeatureFormState {
    name: string;
    description: string;
    arName: string;
    arDescription: string;
    iconValue: string;
}

type EditFeatureFormState = CustomFeatureFormState;

const ServicesSettings: React.FC<ServicesSettingsProps> = ({
    restaurant,
    updateRestaurant,
    branchId
}) => {
    const { t } = useTranslation();
    const [features, setFeatures] = useState<Feature[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
    const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());

    // Custom feature creation states
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [customFeature, setCustomFeature] = useState<CustomFeatureFormState>({
        name: '',
        description: '',
        arName: '',
        arDescription: '',
        iconValue: ''
    });
    const [customFeatureIconMode, setCustomFeatureIconMode] = useState<IconInputMode>('text');
    const [isFeatureIconUploading, setIsFeatureIconUploading] = useState(false);
    const [customChildren, setCustomChildren] = useState<Omit<FeatureChild, 'enabled'>[]>([]);
    const [customChildIconModes, setCustomChildIconModes] = useState<Record<string, IconInputMode>>({});
    const [isCreating, setIsCreating] = useState(false);
    const [uploadingImage, setUploadingImage] = useState<string | null>(null); // Track which child is uploading by id

    // Edit feature states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
    const [editFeatureData, setEditFeatureData] = useState<EditFeatureFormState>({
        name: '',
        description: '',
        arName: '',
        arDescription: '',
        iconValue: ''
    });
    const [editFeatureIconMode, setEditFeatureIconMode] = useState<IconInputMode>('text');
    const [isEditFeatureIconUploading, setIsEditFeatureIconUploading] = useState(false);
    const [editChildren, setEditChildren] = useState<FeatureChild[]>([]);
    const [editChildIconModes, setEditChildIconModes] = useState<Record<string, IconInputMode>>({});
    const [isUpdating, setIsUpdating] = useState(false);

    // Load features for the branch
    useEffect(() => {
        if (branchId) {
            loadFeatures();
        }
    }, [branchId]);

    const loadFeatures = async () => {
        try {
            setLoading(true);
            const branchFeatures = await featureService.getFeaturesByBranch(branchId!);
            setFeatures(branchFeatures);
        } catch (error) {
            console.error('Error loading features:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddFeature = async (template: Omit<Feature, 'id' | 'created_at' | 'updated_at' | 'branch_id'>) => {
        try {
            const newFeature = await featureService.createFeature({
                ...template,
                branch_id: branchId!
            });
            setFeatures(prev => [...prev, newFeature]);
            setShowAddModal(false);
        } catch (error) {
            console.error('Error adding feature:', error);
        }
    };

    const handleCreateCustomFeature = async () => {
        if (!customFeature.name.trim()) return;

        setIsCreating(true);
        try {
            const childrenWithEnabled: FeatureChild[] = customChildren.map(child => ({
                id: child.id as string,
                name: child.name as string,
                description: (child.description as string) || '',
                icon: sanitizeIconValue(child.icon),
                enabled: false,
                ar: child.ar as { name?: string; description?: string } | undefined
            }));

            const iconForSave = sanitizeIconValue(customFeature.iconValue) ?? null;

            const newFeature = await featureService.createFeature({
                name: customFeature.name.trim(),
                description: customFeature.description ? customFeature.description.trim() : null,
                icon: iconForSave,
                children: childrenWithEnabled,
                branch_id: branchId!,
                ar: {
                    name: customFeature.arName || null,
                    description: customFeature.arDescription || null
                }
            });

            setFeatures(prev => [...prev, newFeature]);
            setShowCustomModal(false);
            resetCustomForm();
        } catch (error) {
            console.error('Error creating custom feature:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const resetCustomForm = () => {
        setCustomFeature({
            name: '',
            description: '',
            arName: '',
            arDescription: '',
            iconValue: ''
        });
        setCustomFeatureIconMode('text');
        setCustomChildren([]);
        setCustomChildIconModes({});
        setUploadingImage(null);
        setIsFeatureIconUploading(false);
    };

    const handleCustomFeatureIconUpload = async (file: File) => {
        if (!file) return;

        setIsFeatureIconUploading(true);
        try {
            const fileName = `feature_${Date.now()}_${file.name}`;
            const uploadResult = await uploadToSupabaseStorage(file, fileName, {
                bucket: 'uploads',
                path: 'services'
            });

            setCustomFeature(prev => ({ ...prev, iconValue: uploadResult.publicUrl }));
            setCustomFeatureIconMode('image');
        } catch (error) {
            console.error('Error uploading feature icon:', error);
        } finally {
            setIsFeatureIconUploading(false);
        }
    };

    const handleCustomFeatureIconModeChange = (mode: IconInputMode) => {
        setCustomFeatureIconMode(mode);
        // Don't clear the value when switching modes - let the user keep their data
    };

    const handleChildImageUpload = async (file: File, childIndex: number, childId: string) => {
        if (!file) return;

        setUploadingImage(childId);
        try {
            const fileName = `feature_child_${Date.now()}_${file.name}`;
            const uploadResult = await uploadToSupabaseStorage(file, fileName, {
                bucket: 'uploads',
                path: 'services'
            });

            updateCustomChild(childIndex, { icon: uploadResult.publicUrl });
            setCustomChildIconModes(prev => ({ ...prev, [childId]: 'image' }));
        } catch (error) {
            console.error('Error uploading image:', error);
        } finally {
            setUploadingImage(null);
        }
    };

    const addCustomChild = () => {
        const newChildId = `child_${Date.now()}`;
        const newChild: Omit<FeatureChild, 'enabled'> = {
            id: newChildId,
            name: '',
            description: '',
            icon: undefined,
            ar: {
                name: '',
                description: ''
            }
        };

        setCustomChildren(prev => [...prev, newChild]);
        setCustomChildIconModes(prev => ({ ...prev, [newChildId]: 'text' }));
    };

    const updateCustomChild = (index: number, updates: Partial<Omit<FeatureChild, 'enabled'>>) => {
        setCustomChildren(prev => prev.map((child, i) =>
            i === index ? { ...child, ...updates } : child
        ));
    };

    const handleCustomChildIconModeChange = (childId: string, mode: IconInputMode, index: number) => {
        setCustomChildIconModes(prev => ({ ...prev, [childId]: mode }));
        // Don't clear the icon value when switching modes - preserve the data
    };

    const removeCustomChild = (index: number) => {
        const childId = customChildren[index]?.id as string | undefined;
        setCustomChildren(prev => prev.filter((_, i) => i !== index));
        if (childId) {
            setCustomChildIconModes(prev => {
                const next = { ...prev };
                delete next[childId];
                return next;
            });
        }
    };

    // Edit feature functions
    const handleEditFeature = (feature: Feature) => {
        const childList = getFeatureChildren(feature);

        setEditingFeature(feature);
        setEditFeatureData({
            name: feature.name,
            description: feature.description || '',
            arName: getFeatureArName(feature),
            arDescription: getFeatureArDescription(feature),
            iconValue: feature.icon || ''
        });
        setEditFeatureIconMode(determineIconMode(feature.icon));
        setEditChildren(childList);

        const initialModes: Record<string, IconInputMode> = {};
        childList.forEach(child => {
            initialModes[child.id] = determineIconMode(child.icon);
        });
        setEditChildIconModes(initialModes);

        setShowEditModal(true);
    };

    const handleEditFeatureIconUpload = async (file: File) => {
        if (!file) return;

        setIsEditFeatureIconUploading(true);
        try {
            const fileName = `feature_${Date.now()}_${file.name}`;
            const uploadResult = await uploadToSupabaseStorage(file, fileName, {
                bucket: 'uploads',
                path: 'services'
            });

            setEditFeatureData(prev => ({ ...prev, iconValue: uploadResult.publicUrl }));
            setEditFeatureIconMode('image');
        } catch (error) {
            console.error('Error uploading feature icon:', error);
        } finally {
            setIsEditFeatureIconUploading(false);
        }
    };

    const handleEditFeatureIconModeChange = (mode: IconInputMode) => {
        setEditFeatureIconMode(mode);
        // Don't clear the value when switching modes - let the user keep their data
    };

    const handleUpdateFeature = async () => {
        if (!editingFeature || !editFeatureData.name.trim()) return;

        setIsUpdating(true);
        try {
            const sanitizedChildren = prepareChildrenForSave(editChildren);
            const featureIcon = sanitizeIconValue(editFeatureData.iconValue) ?? null;

            const updatedFeature = await featureService.updateFeature(editingFeature.id, {
                name: editFeatureData.name.trim(),
                description: editFeatureData.description ? editFeatureData.description.trim() : null,
                icon: featureIcon,
                children: featureChildrenToJson(sanitizedChildren),
                ar: {
                    name: editFeatureData.arName || null,
                    description: editFeatureData.arDescription || null
                }
            });

            setFeatures(prev => prev.map(f =>
                f.id === editingFeature.id ? { ...f, ...updatedFeature } : f
            ));

            setShowEditModal(false);
            resetEditForm();
        } catch (error) {
            console.error('Error updating feature:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const resetEditForm = () => {
        setEditingFeature(null);
        setEditFeatureData({
            name: '',
            description: '',
            arName: '',
            arDescription: '',
            iconValue: ''
        });
        setEditFeatureIconMode('text');
        setEditChildren([]);
        setEditChildIconModes({});
        setUploadingImage(null);
        setIsEditFeatureIconUploading(false);
    };

    const addEditChild = () => {
        const newChildId = `child_${Date.now()}`;
        const newChild: FeatureChild = {
            id: newChildId,
            name: '',
            description: '',
            icon: undefined,
            enabled: false,
            ar: {
                name: '',
                description: ''
            }
        };

        setEditChildren(prev => [...prev, newChild]);
        setEditChildIconModes(prev => ({ ...prev, [newChildId]: 'text' }));
    };

    const updateEditChild = (index: number, updates: Partial<FeatureChild>) => {
        setEditChildren(prev => prev.map((child, i) =>
            i === index ? { ...child, ...updates } : child
        ));
    };

    const handleEditChildIconModeChange = (childId: string, mode: IconInputMode, index: number) => {
        setEditChildIconModes(prev => ({ ...prev, [childId]: mode }));
        // Don't clear the icon value when switching modes - preserve the data
    };

    const removeEditChild = (index: number) => {
        const childId = editChildren[index]?.id as string | undefined;
        setEditChildren(prev => prev.filter((_, i) => i !== index));
        if (childId) {
            setEditChildIconModes(prev => {
                const next = { ...prev };
                delete next[childId];
                return next;
            });
        }
    };

    const handleEditChildImageUpload = async (file: File, childIndex: number, childId: string) => {
        if (!file) return;

        setUploadingImage(childId);
        try {
            const fileName = `feature_child_${Date.now()}_${file.name}`;
            const uploadResult = await uploadToSupabaseStorage(file, fileName, {
                bucket: 'uploads',
                path: 'services'
            });

            updateEditChild(childIndex, { icon: uploadResult.publicUrl });
            setEditChildIconModes(prev => ({ ...prev, [childId]: 'image' }));
        } catch (error) {
            console.error('Error uploading image:', error);
        } finally {
            setUploadingImage(null);
        }
    };

    const handleDeleteFeature = async (featureId: string) => {
        try {
            await featureService.deleteFeature(featureId);
            setFeatures(prev => prev.filter(f => f.id !== featureId));
            setShowDeleteModal(null);
        } catch (error) {
            console.error('Error deleting feature:', error);
        }
    };

    const handleToggleFeatureChild = async (feature: Feature, childId: string) => {
        try {
            const children = getFeatureChildren(feature);
            const updatedChildren = children.map(child =>
                child.id === childId
                    ? { ...child, enabled: !child.enabled }
                    : child
            );

            await featureService.updateFeatureChildren(feature.id, featureChildrenToJson(updatedChildren));
            setFeatures(prev => prev.map(f =>
                f.id === feature.id
                    ? { ...f, children: featureChildrenToJson(updatedChildren) }
                    : f
            ));
        } catch (error) {
            console.error('Error updating feature settings:', error);
        }
    };

    const toggleFeatureExpansion = (featureId: string) => {
        setExpandedFeatures(prev => {
            const newSet = new Set(prev);
            if (newSet.has(featureId)) {
                newSet.delete(featureId);
            } else {
                newSet.add(featureId);
            }
            return newSet;
        });
    };

    const getFeatureIcon = (iconName?: string | null) => {
        if (!iconName || !ICON_MAP[iconName]) {
            return FiSettings;
        }
        return ICON_MAP[iconName];
    };

    const renderFeatureIcon = (iconValue?: string | null, className = "h-5 w-5") => {
        if (isImageIconValue(iconValue)) {
            return (
                <img
                    src={iconValue!}
                    alt="Feature icon"
                    className={`${className} object-cover rounded`}
                />
            );
        }

        if (!iconValue) {
            const DefaultIcon = FiSettings;
            return <DefaultIcon className={`${className} text-primary`} />;
        }

        if (ICON_MAP[iconValue]) {
            const IconComponent = getFeatureIcon(iconValue);
            return <IconComponent className={`${className} text-primary`} />;
        }

        if (/\s/.test(iconValue) || iconValue.startsWith('fa')) {
            return (
                <span className={`inline-flex items-center justify-center ${className}`}>
                    <i className={`${iconValue} text-primary`} aria-hidden="true" />
                </span>
            );
        }

        return (
            <span className={`inline-flex items-center justify-center ${className} text-primary text-sm font-semibold leading-none`}>
                {iconValue.length <= 3 ? iconValue : iconValue.slice(0, 3)}
            </span>
        );
    };

    const getIconModeButtonClasses = (isActive: boolean) => [
        'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
        isActive
            ? 'bg-primary text-white border-primary hover:bg-primary/90'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
    ].join(' ');

    const getEnabledCount = (children: any): number => {
        if (!Array.isArray(children)) return 0;
        return children.filter((child: FeatureChild) => child.enabled).length;
    };

    const getAvailableTemplates = () => {
        const existingFeatureNames = new Set(features.map(f => f.name));
        return FEATURE_TEMPLATES.filter(template => !existingFeatureNames.has(template.name));
    };

    // Helper functions to get localized text
    const getFeatureName = (feature: Feature, language: string): string => {
        if (language === 'ar') {
            const arName = getFeatureArName(feature);
            if (arName) return arName;
        }
        return feature.name;
    };

    const getFeatureDescription = (feature: Feature, language: string): string => {
        if (language === 'ar') {
            const arDescription = getFeatureArDescription(feature);
            if (arDescription) return arDescription;
        }
        return feature.description || '';
    };

    const getChildName = (child: FeatureChild, language: string): string => {
        if (language === 'ar' && child.ar?.name) {
            return child.ar.name;
        }
        return child.name;
    };

    const getChildDescription = (child: FeatureChild, language: string): string => {
        if (language === 'ar' && child.ar?.description) {
            return child.ar.description;
        }
        return child.description || '';
    };

    if (loading) {
        return (
            <Card>
                <div className="animate-pulse space-y-4">
                    <div className="h-4  rounded w-1/4"></div>
                    <div className="space-y-3">
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {t('settings.services.title')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('settings.services.subtitle')}
                        </p>
                    </div>
                    <Button
                        onClick={() => setShowAddModal(true)}
                        leftIcon={<FiPlus />}
                        disabled={getAvailableTemplates().length === 0}
                    >
                        {t('settings.services.addFeature')}
                    </Button>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6">
                    {features.length === 0 ? (
                        <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                            <FiSettings className="mx-auto h-12 w-12 mb-4 opacity-50" />
                            <h3 className="text-lg font-medium mb-2">{t('settings.services.noFeatures')}</h3>
                            <p className="text-sm">{t('settings.services.noFeaturesDescription')}</p>
                        </div>
                    ) : (
                        features.map((feature) => {
                            const isExpanded = expandedFeatures.has(feature.id);
                            const children = getFeatureChildren(feature);
                            const enabledCount = getEnabledCount(children);

                            return (
                                <div key={feature.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                    {/* Feature Header */}
                                    <div
                                        className="p-4 bg-white dark:bg-gray-800 cursor-pointer  dark:hover:bg-gray-700 transition-colors"
                                        onClick={() => handleEditFeature(feature)}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center space-x-3 flex-1">
                                                <div className="flex-shrink-0">
                                                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                                        {renderFeatureIcon(feature.icon)}
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                                        {getFeatureName(feature, i18n.language)}
                                                    </h4>
                                                    {i18n.language === 'ar' && feature.name && (
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            {feature.name}
                                                        </p>
                                                    )}
                                                    {getFeatureDescription(feature, i18n.language) && (
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                                            {getFeatureDescription(feature, i18n.language)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end space-y-2 ml-2" onClick={(e) => e.stopPropagation()}>
                                                {children.length > 0 && (
                                                    <Badge
                                                        variant={enabledCount > 0 ? "success" : "secondary"}
                                                        className="text-xs"
                                                    >
                                                        {enabledCount}/{children.length}
                                                    </Badge>
                                                )}
                                                <div className="flex space-x-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditFeature(feature);
                                                        }}
                                                        className="p-2 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200"
                                                        title={t('settings.services.editFeature')}
                                                    >
                                                        <FiEdit2 className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowDeleteModal(feature.id);
                                                        }}
                                                        className="p-2 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                                                        title={t('settings.services.deleteFeature')}
                                                    >
                                                        <FiTrash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Feature Settings Toggle */}
                                        {children.length > 0 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleFeatureExpansion(feature.id);
                                                }}
                                                className="w-full text-left text-xs text-primary hover:text-primary-dark transition-colors cursor-pointer "
                                            >
                                                {isExpanded ? t('settings.services.hideSettings') : t('settings.services.showSettings')}
                                            </button>
                                        )}
                                    </div>

                                    {/* Feature Settings */}
                                    {isExpanded && children.length > 0 && (
                                        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                                            <div className="p-3 space-y-2">
                                                {children.map((child) => (
                                                    <div key={child.id} className="flex items-center justify-between py-1">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                                                                {getChildName(child, i18n.language)}
                                                            </div>
                                                            {i18n.language === 'ar' && child.name && (
                                                                <div className="text-xs text-gray-400 mt-0.5">
                                                                    {child.name}
                                                                </div>
                                                            )}
                                                            {getChildDescription(child, i18n.language) && (
                                                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                                                                    {getChildDescription(child, i18n.language)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Toggle
                                                            enabled={child.enabled}
                                                            onChange={() => handleToggleFeatureChild(feature, child.id)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </Card>

            {/* Add Feature Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto scrollbar-hide">
                        <div className="flex items-center justify-between mb-4 ">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white ">
                                {t('settings.services.addFeature')}
                            </h3>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-white hover:text-white rounded-full bg-red-500 hover:bg-red-600 w-6 h-6 flex items-center justify-center text-sm font-bold cursor-pointer "
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Custom Feature Option */}
                            <div
                                className="border-2 border-dashed border-primary border-opacity-50 rounded-lg p-4 cursor-pointer hover:bg-primary/5 transition-colors "
                                onClick={() => {
                                    setShowAddModal(false);
                                    setShowCustomModal(true);
                                }}
                            >
                                <div className="flex items-center space-x-3 ">
                                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                        <FiPlus className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-medium text-gray-900 dark:text-white">
                                            {t('settings.services.createCustomFeature')}
                                        </h4>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {t('settings.services.createCustomFeature')}
                                        </p>
                                    </div>
                                    <FiEdit2 className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="flex items-center">
                                <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
                                <span className="px-3 text-sm text-gray-500 dark:text-gray-400">or choose a template</span>
                                <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
                            </div>

                            {/* Template Options */}
                            {getAvailableTemplates().map((template, index) => {
                                return (
                                    <div
                                        key={index}
                                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        onClick={() => handleAddFeature(template)}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                                {renderFeatureIcon(template.icon)}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-900 dark:text-white">
                                                    {template.name}
                                                </h4>
                                                {template.description && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {template.description}
                                                    </p>
                                                )}
                                            </div>
                                            <FiPlus className="h-5 w-5 text-primary" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {getAvailableTemplates().length === 0 && (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <p>{t('settings.services.allFeaturesAdded')}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Custom Feature Creation Modal */}
            {showCustomModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-hide">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {t('settings.services.createCustomFeature')}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowCustomModal(false);
                                    resetCustomForm();
                                }}
                                className="text-white hover:text-white rounded-full bg-red-500 hover:bg-red-600 w-6 h-6 flex items-center justify-center text-sm font-bold cursor-pointer"
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Basic Feature Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                    {t('settings.services.featureInformation')}
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputField
                                        label={t('settings.services.featureName')}
                                        placeholder={t('settings.services.enterFeatureName')}
                                        value={customFeature.name}
                                        onChange={(e) => setCustomFeature(prev => ({ ...prev, name: e.target.value }))}
                                        required
                                    />

                                    <InputField
                                        label="اسم الخدمة (بالعربية)"
                                        placeholder="أدخل اسم الخدمة بالعربية"
                                        value={customFeature.arName}
                                        onChange={(e) => setCustomFeature(prev => ({ ...prev, arName: e.target.value }))}
                                        dir="rtl"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('settings.services.featureDescription')}
                                        </label>
                                        <textarea
                                            placeholder={t('settings.services.enterFeatureDescription')}
                                            value={customFeature.description}
                                            onChange={(e) => setCustomFeature(prev => ({ ...prev, description: e.target.value }))}
                                            rows={3}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            وصف الخدمة (بالعربية)
                                        </label>
                                        <textarea
                                            placeholder="أدخل وصف الخدمة بالعربية"
                                            value={customFeature.arDescription}
                                            onChange={(e) => setCustomFeature(prev => ({ ...prev, arDescription: e.target.value }))}
                                            rows={3}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            dir="rtl"
                                        />
                                    </div>
                                </div>

                                {/* Feature Icon */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('settings.services.featureIcon')}
                                    </label>

                                    {/* Icon Mode Toggle */}
                                    <div className="flex space-x-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => handleCustomFeatureIconModeChange('text')}
                                            className={getIconModeButtonClasses(customFeatureIconMode === 'text')}
                                        >
                                            {t('settings.services.textClass')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleCustomFeatureIconModeChange('image')}
                                            className={getIconModeButtonClasses(customFeatureIconMode === 'image')}
                                        >
                                            {t('settings.services.uploadImage')}
                                        </button>
                                    </div>                                    {/* Icon Input */}
                                    {customFeatureIconMode === 'text' ? (
                                        <InputField
                                            placeholder="e.g., FiStar or fa fa-star"
                                            value={customFeature.iconValue}
                                            onChange={(e) => setCustomFeature(prev => ({ ...prev, iconValue: e.target.value }))}
                                        />
                                    ) : (
                                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                                            {isImageIconValue(customFeature.iconValue) ? (
                                                <div className="relative inline-block">
                                                    <img
                                                        src={customFeature.iconValue}
                                                        alt="Feature icon preview"
                                                        className="w-20 h-20 object-cover rounded"
                                                    />
                                                    <button
                                                        onClick={() => setCustomFeature(prev => ({ ...prev, iconValue: '' }))}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                    >
                                                        <FiTrash2 className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <label
                                                        htmlFor="feature-icon-upload"
                                                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                    >
                                                        {isFeatureIconUploading ? t('settings.services.uploading') : t('settings.services.chooseImage')}
                                                    </label>
                                                    <input
                                                        id="feature-icon-upload"
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) handleCustomFeatureIconUpload(file);
                                                        }}
                                                        disabled={isFeatureIconUploading}
                                                    />
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                        PNG, JPG, WEBP (recommended: 100x100px)
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sub-features */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-gray-900 dark:text-white">
                                        {t('settings.services.subfeatureOptional')}
                                    </h4>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={addCustomChild}
                                        leftIcon={<FiPlus />}
                                    >
                                        {t('settings.services.addSubfeature')}
                                    </Button>
                                </div>

                                {customChildren.map((child, index) => (
                                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {t('settings.services.subfeature')} {index + 1}
                                            </span>
                                            <button
                                                onClick={() => removeCustomChild(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <FiTrash2 className="h-4 w-4" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <input
                                                type="text"
                                                placeholder="Sub-feature name"
                                                value={child.name as string || ''}
                                                onChange={(e) => updateCustomChild(index, { name: e.target.value })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />

                                            <input
                                                type="text"
                                                placeholder="اسم الخدمة الفرعية (بالعربية)"
                                                value={getFeatureChildArName(child)}
                                                onChange={(e) => updateCustomChild(index, {
                                                    ar: {
                                                        name: e.target.value,
                                                        description: getFeatureChildArDescription(child)
                                                    }
                                                })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                dir="rtl"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <input
                                                type="text"
                                                placeholder="Description"
                                                value={child.description as string || ''}
                                                onChange={(e) => updateCustomChild(index, { description: e.target.value })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />

                                            <input
                                                type="text"
                                                placeholder="وصف الخدمة الفرعية (بالعربية)"
                                                value={getFeatureChildArDescription(child)}
                                                onChange={(e) => updateCustomChild(index, {
                                                    ar: {
                                                        name: getFeatureChildArName(child),
                                                        description: e.target.value
                                                    }
                                                })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                dir="rtl"
                                            />
                                        </div>

                                        {/* Sub-feature Icon */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                                {t('settings.services.subfeatureImage')}
                                            </label>

                                            {/* Icon Mode Toggle */}
                                            <div className="flex space-x-2 mb-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCustomChildIconModeChange(child.id as string, 'text', index)}
                                                    className={getIconModeButtonClasses(customChildIconModes[child.id as string] === 'text')}
                                                >
                                                    {t('settings.services.textClass')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCustomChildIconModeChange(child.id as string, 'image', index)}
                                                    className={getIconModeButtonClasses(customChildIconModes[child.id as string] === 'image')}
                                                >
                                                    {t('settings.services.uploadImage')}
                                                </button>
                                            </div>                                            {/* Icon Input */}
                                            {customChildIconModes[child.id as string] === 'image' ? (
                                                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3">
                                                    {isImageIconValue(child.icon) ? (
                                                        <div className="relative inline-block">
                                                            <img
                                                                src={child.icon as string}
                                                                alt="Sub-feature preview"
                                                                className="w-16 h-16 object-cover rounded"
                                                            />
                                                            <button
                                                                onClick={() => updateCustomChild(index, { icon: undefined })}
                                                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                            >
                                                                <FiTrash2 className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center">
                                                            <label
                                                                htmlFor={`sub-feature-image-${index}`}
                                                                className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                            >
                                                                {uploadingImage === child.id ? t('settings.services.uploading') : t('settings.services.uploadImage')}
                                                            </label>
                                                            <input
                                                                id={`sub-feature-image-${index}`}
                                                                type="file"
                                                                className="hidden"
                                                                accept="image/*"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) handleChildImageUpload(file, index, child.id as string);
                                                                }}
                                                                disabled={uploadingImage === child.id}
                                                            />
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                PNG, JPG, WEBP
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    placeholder="e.g., FiTruck or fa fa-truck"
                                                    value={(child.icon as string) || ''}
                                                    onChange={(e) => updateCustomChild(index, { icon: e.target.value })}
                                                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full text-sm"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {customChildren.length === 0 && (
                                    <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                                        <p>{t('settings.services.noSubfeaturesAdded')}</p>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowCustomModal(false);
                                        resetCustomForm();
                                    }}
                                >
                                    {t('settings.services.cancel')}
                                </Button>
                                <Button
                                    onClick={handleCreateCustomFeature}
                                    isLoading={isCreating}
                                    disabled={!customFeature.name.trim()}
                                    leftIcon={<FiPlus />}
                                >
                                    {t('settings.services.createFeature')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Feature Modal */}
            {showEditModal && editingFeature && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-hide">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                {t('settings.services.editFeature')}: {editingFeature.name}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    resetEditForm();
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Basic Feature Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                    {t('settings.services.featureInformation')}
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputField
                                        label={t('settings.services.featureName')}
                                        placeholder={t('settings.services.enterFeatureName')}
                                        value={editFeatureData.name}
                                        onChange={(e) => setEditFeatureData(prev => ({ ...prev, name: e.target.value }))}
                                        required
                                    />

                                    <InputField
                                        label="اسم الخدمة (بالعربية)"
                                        placeholder="أدخل اسم الخدمة بالعربية"
                                        value={editFeatureData.arName}
                                        onChange={(e) => setEditFeatureData(prev => ({ ...prev, arName: e.target.value }))}
                                        dir="rtl"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('settings.services.featureDescription')}
                                        </label>
                                        <textarea
                                            placeholder={t('settings.services.enterFeatureDescription')}
                                            value={editFeatureData.description}
                                            onChange={(e) => setEditFeatureData(prev => ({ ...prev, description: e.target.value }))}
                                            rows={3}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            وصف الخدمة (بالعربية)
                                        </label>
                                        <textarea
                                            placeholder="أدخل وصف الخدمة بالعربية"
                                            value={editFeatureData.arDescription}
                                            onChange={(e) => setEditFeatureData(prev => ({ ...prev, arDescription: e.target.value }))}
                                            rows={3}
                                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            dir="rtl"
                                        />
                                    </div>
                                </div>

                                {/* Feature Icon */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('settings.services.featureIcon')}
                                    </label>

                                    {/* Icon Mode Toggle */}
                                    <div className="flex space-x-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => handleEditFeatureIconModeChange('text')}
                                            className={getIconModeButtonClasses(editFeatureIconMode === 'text')}
                                        >
                                            {t('settings.services.textClass')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleEditFeatureIconModeChange('image')}
                                            className={getIconModeButtonClasses(editFeatureIconMode === 'image')}
                                        >
                                            {t('settings.services.uploadImage')}
                                        </button>
                                    </div>                                    {/* Icon Input */}
                                    {editFeatureIconMode === 'text' ? (
                                        <InputField
                                            placeholder="e.g., FiStar or fa fa-star"
                                            value={editFeatureData.iconValue}
                                            onChange={(e) => setEditFeatureData(prev => ({ ...prev, iconValue: e.target.value }))}
                                        />
                                    ) : (
                                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                                            {isImageIconValue(editFeatureData.iconValue) ? (
                                                <div className="relative inline-block">
                                                    <img
                                                        src={editFeatureData.iconValue}
                                                        alt="Feature icon preview"
                                                        className="w-20 h-20 object-cover rounded"
                                                    />
                                                    <button
                                                        onClick={() => setEditFeatureData(prev => ({ ...prev, iconValue: '' }))}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                    >
                                                        <FiTrash2 className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <label
                                                        htmlFor="edit-feature-icon-upload"
                                                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                    >
                                                        {isEditFeatureIconUploading ? t('settings.services.uploading') : t('settings.services.chooseImage')}
                                                    </label>
                                                    <input
                                                        id="edit-feature-icon-upload"
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) handleEditFeatureIconUpload(file);
                                                        }}
                                                        disabled={isEditFeatureIconUploading}
                                                    />
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                        PNG, JPG, WEBP (recommended: 100x100px)
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sub-features */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-gray-900 dark:text-white">
                                        {t('settings.services.subfeatures')}
                                    </h4>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={addEditChild}
                                        leftIcon={<FiPlus />}
                                    >
                                        {t('settings.services.addSubfeature')}
                                    </Button>
                                </div>

                                {editChildren.map((child, index) => (
                                    <div key={child.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {t('settings.services.subfeature')} {index + 1}
                                            </span>
                                            <button
                                                onClick={() => removeEditChild(index)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <FiTrash2 className="h-4 w-4" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <input
                                                type="text"
                                                placeholder="Sub-feature name"
                                                value={child.name || ''}
                                                onChange={(e) => updateEditChild(index, { name: e.target.value })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />

                                            <input
                                                type="text"
                                                placeholder="اسم الخدمة الفرعية (بالعربية)"
                                                value={child.ar?.name || ''}
                                                onChange={(e) => updateEditChild(index, {
                                                    ar: {
                                                        ...child.ar,
                                                        name: e.target.value
                                                    }
                                                })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                dir="rtl"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <input
                                                type="text"
                                                placeholder="Description"
                                                value={child.description || ''}
                                                onChange={(e) => updateEditChild(index, { description: e.target.value })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />

                                            <input
                                                type="text"
                                                placeholder="وصف الخدمة الفرعية (بالعربية)"
                                                value={child.ar?.description || ''}
                                                onChange={(e) => updateEditChild(index, {
                                                    ar: {
                                                        ...child.ar,
                                                        description: e.target.value
                                                    }
                                                })}
                                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                dir="rtl"
                                            />
                                        </div>

                                        {/* Enabled Toggle */}
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                                {t('settings.services.enabled')}
                                            </span>
                                            <Toggle
                                                enabled={child.enabled}
                                                onChange={() => updateEditChild(index, { enabled: !child.enabled })}
                                            />
                                        </div>

                                        {/* Sub-feature Icon */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                                {t('settings.services.subfeatureImage')}
                                            </label>

                                            {/* Icon Mode Toggle */}
                                            <div className="flex space-x-2 mb-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditChildIconModeChange(child.id, 'text', index)}
                                                    className={getIconModeButtonClasses(editChildIconModes[child.id] === 'text')}
                                                >
                                                    {t('settings.services.textClass')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditChildIconModeChange(child.id, 'image', index)}
                                                    className={getIconModeButtonClasses(editChildIconModes[child.id] === 'image')}
                                                >
                                                    {t('settings.services.uploadImage')}
                                                </button>
                                            </div>                                            {/* Icon Input */}
                                            {editChildIconModes[child.id] === 'image' ? (
                                                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3">
                                                    {isImageIconValue(child.icon) ? (
                                                        <div className="relative inline-block">
                                                            <img
                                                                src={child.icon}
                                                                alt="Sub-feature preview"
                                                                className="w-16 h-16 object-cover rounded"
                                                            />
                                                            <button
                                                                onClick={() => updateEditChild(index, { icon: undefined })}
                                                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                            >
                                                                <FiTrash2 className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center">
                                                            <label
                                                                htmlFor={`edit-sub-feature-image-${index}`}
                                                                className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                            >
                                                                {uploadingImage === child.id ? t('settings.services.uploading') : t('settings.services.uploadImage')}
                                                            </label>
                                                            <input
                                                                id={`edit-sub-feature-image-${index}`}
                                                                type="file"
                                                                className="hidden"
                                                                accept="image/*"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) handleEditChildImageUpload(file, index, child.id);
                                                                }}
                                                                disabled={uploadingImage === child.id}
                                                            />
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                PNG, JPG, WEBP
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    placeholder="e.g., FiTruck or fa fa-truck"
                                                    value={child.icon || ''}
                                                    onChange={(e) => updateEditChild(index, { icon: e.target.value })}
                                                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full text-sm"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {editChildren.length === 0 && (
                                    <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                                        <p>{t('settings.services.noSubfeaturesAdded')}</p>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        resetEditForm();
                                    }}
                                >
                                    {t('settings.services.cancel')}
                                </Button>
                                <Button
                                    onClick={handleUpdateFeature}
                                    isLoading={isUpdating}
                                    disabled={!editFeatureData.name.trim()}
                                    leftIcon={<FiEdit2 />}
                                >
                                    {t('settings.services.updateFeature')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <ConfirmDeleteModal
                    isOpen={!!showDeleteModal}
                    onCancel={() => setShowDeleteModal(null)}
                    onConfirm={() => handleDeleteFeature(showDeleteModal)}
                    title={t('settings.services.deleteFeature')}
                    message={t('settings.services.deleteFeatureConfirmation')}
                />
            )}
        </div>
    );
};

export default ServicesSettings;